'use client';

import classNames from 'classnames';
import React from 'react';
import styled from 'styled-components';

export interface SkeletonProps {
  active?: boolean;
  title?: boolean;
  variant?: 'box' | 'paragraph' | 'card' | 'moon-or-bust-card';
  text?: string;
  paragraph?: Paragraph;
}

export type Paragraph = {
  rows: number;
  width?: number[] | string[];
};

export const Skeleton: React.FC<SkeletonProps> = ({
  active = false,
  paragraph,
  text,
  title,
  variant = 'paragraph',
}) => {
  const generateSkeletons = React.useCallback((rows = 3) => {
    const result = Array(rows).fill(null);
    return result;
  }, []);

  const skeletonChildren = generateSkeletons(paragraph?.rows);
  const width = paragraph?.width;

  if (variant === 'card') {
    return (
      <div className="w-full min-h-[inherit]">
        <div className="w-full h-full min-h-[inherit] flex items-center justify-center mx-auto rounded-md border border-gray-300 p-4 shadow">
          <div className="flex flex-col md:flex-row animate-pulse w-full gap-4">
            <div className="icon-wrapper flex items-center justify-center mx-6">
              <svg
                aria-hidden="true"
                className="w-16 h-16 text-gray-400"
                fill="currentColor"
                viewBox="0 0 640 512"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M480 80C480 35.82 515.8 0 560 0C604.2 0 640 35.82 640 80C640 124.2 604.2 160 560 160C515.8 160 480 124.2 480 80zM0 456.1C0 445.6 2.964 435.3 8.551 426.4L225.3 81.01C231.9 70.42 243.5 64 256 64C268.5 64 280.1 70.42 286.8 81.01L412.7 281.7L460.9 202.7C464.1 196.1 472.2 192 480 192C487.8 192 495 196.1 499.1 202.7L631.1 419.1C636.9 428.6 640 439.7 640 450.9C640 484.6 612.6 512 578.9 512H55.91C25.03 512 .0006 486.1 .0006 456.1L0 456.1z"></path>
              </svg>
            </div>
            <div className="paragraphs-wrapper w-full">
              <div className="flex-1 space-y-6 py-1">
                <div className="h-3 rounded bg-gray-300"></div>
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2 h-2 rounded bg-gray-300"></div>
                    <div className="col-span-1 h-2 rounded bg-gray-300"></div>
                  </div>
                  <div className="h-2 rounded bg-gray-300"></div>
                  <div className="h-2 rounded bg-gray-300"></div>
                  <div className="h-2 rounded bg-gray-300"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'moon-or-bust-card') {
    return (
      <div className="min-h-[380px] md:min-h-[380px] max-w-[400px] m-auto">
        <div className="w-full min-h-[inherit]">
          <div className="w-full h-full min-h-[inherit] flex items-center justify-center mx-auto border border-[#E1EBFA] p-4 cursor-default rounded-sm">
            <div className="flex flex-col animate-pulse w-full gap-1">
              <div className="icon-wrapper flex items-center justify-center mx-6">
                <svg
                  aria-hidden="true"
                  className="w-16 h-16 text-gray-400"
                  fill="currentColor"
                  viewBox="0 0 640 512"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M480 80C480 35.82 515.8 0 560 0C604.2 0 640 35.82 640 80C640 124.2 604.2 160 560 160C515.8 160 480 124.2 480 80zM0 456.1C0 445.6 2.964 435.3 8.551 426.4L225.3 81.01C231.9 70.42 243.5 64 256 64C268.5 64 280.1 70.42 286.8 81.01L412.7 281.7L460.9 202.7C464.1 196.1 472.2 192 480 192C487.8 192 495 196.1 499.1 202.7L631.1 419.1C636.9 428.6 640 439.7 640 450.9C640 484.6 612.6 512 578.9 512H55.91C25.03 512 .0006 486.1 .0006 456.1L0 456.1z"></path>
                </svg>
              </div>
              <div className="paragraphs-wrapper w-60 m-auto">
                <div className="flex-1 py-0">
                  <div className="h-8 rounded bg-gray-300 my-2 w-3/5 m-auto"></div>
                  <div className="h-8 rounded bg-gray-300 my-2 w-3/5 m-auto"></div>
                  <div className="h-3 rounded bg-gray-300 my-2 w-3/4 m-auto"></div>
                  <div className="h-6 rounded bg-gray-300 w-1/4 m-auto my-2"></div>
                  <div className="h-5 rounded bg-gray-300 w-4 my-2 m-auto"></div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="h-[40px] rounded bg-gray-300"></div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="col-span-1 h-[40px] rounded bg-gray-300"></div>
                  <div className="col-span-1 h-[40px] rounded bg-gray-300"></div>
                </div>
                <div className="h-5 rounded bg-gray-300 w-20 m-auto"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Container
      className={classNames('skeleton min-h-[inherit]', { 'animate-pulse': active, variant: !!variant })}
      role="status"
    >
      {variant === 'box' && (
        <div className="skeleton-child h-full w-full min-h-[inherit] flex items-center justify-center">
          <span>{text}</span>
        </div>
      )}
      {variant === 'paragraph' && (
        <>
          {title && <div className="skeleton-child first-skeleton"></div>}
          {skeletonChildren.map((skeletonChild, index) => {
            const style = width && width[index] ? { width: width[index] } : undefined;
            return (
              <div className="skeleton-child" key={index} style={style}>
                {skeletonChild}
              </div>
            );
          })}
        </>
      )}
      <span className="sr-only">Loading...</span>
    </Container>
  );
};

export default Skeleton;

const Container = styled.div`
  &.skeleton {
    &.paragraph {
      .skeleton-child {
        &.first-skeleton {
          width: 38%;
        }

        &:last-of-type {
          width: 61%;
        }
      }
    }

    .skeleton-child {
      background-color: ${({ theme }) => theme.colorPalette.gray700};
      background-color: ${({ theme }) => theme.colorPalette.gray200};
      border-radius: ${({ theme }) => theme.borderRadius.sm};
      width: 100%;
      height: 0.825rem;
      margin-bottom: 1rem;
    }
  }
`;
